import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import EditEventModal from '../components/EditEventModal';
import DeleteEventModal from '../components/DeleteEventModal';
import { useAppContext } from '../context/AppContext';
import { API_BASE_URL } from '../constants/api';
import './DashboardPage.css';

// Helper function to safely render HTML content
const renderHtmlContent = (content: string) => {
  if (!content) return 'No description';
  return <div dangerouslySetInnerHTML={{ __html: content }} />;
};

type Event = {
  id: number;
  event_Name: string;
  start_Datetime: string;
  end_Datetime: string;
  event_Img?: string;
  description: string;
  status: string;
  submitted_By?: string;
  submitted_Email?: string;
  created_At?: string;
};

interface EventsPageProps {
  events: Event[];
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onAddEvent: (event: Event) => void; // new prop
  defaultFilter?: 'All' | 'Scheduled' | 'Completed' | 'Pending';
}

const EventsPage: React.FC<EventsPageProps> = ({ events, onEdit, onDelete, onAddEvent, defaultFilter = 'All' }) => {
  const { user } = useAppContext();
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    description: '',
    image: ''
  });
  const [addEventError, setAddEventError] = useState('');
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [statusFilter, setStatusFilter] = useState<'All' | 'Scheduled' | 'Completed' | 'Pending'>(defaultFilter);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [searchTerm, setSearchTerm] = useState('');

  // Update filter when defaultFilter prop changes
  useEffect(() => {
    setStatusFilter(defaultFilter);
  }, [defaultFilter]);
  // Modal state for edit and delete
  const [editEventModalOpen, setEditEventModalOpen] = useState(false);
  const [editEvent, setEditEvent] = useState<Event | null>(null);
  const [deleteEventModalOpen, setDeleteEventModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setAddEventError('Please select a valid image file.');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setAddEventError('Image file size must be less than 5MB.');
        return;
      }

      setImageFile(file);
      setAddEventError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }

    // Clear the input value so the same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#4f8cff';
    e.currentTarget.style.backgroundColor = '#f8faff';
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#e0e6f0';
    e.currentTarget.style.backgroundColor = '#fff';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#e0e6f0';
    e.currentTarget.style.backgroundColor = '#fff';

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        setAddEventError('Image file size must be less than 5MB.');
        return;
      }

      setImageFile(file);
      setAddEventError('');

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setAddEventError('Please drop a valid image file.');
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setAddEventError('');
  };

  const handleAddEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !newEvent.name.trim() ||
      !newEvent.startDate.trim() ||
      !newEvent.startTime.trim() ||
      !newEvent.endDate.trim() ||
      !newEvent.endTime.trim() ||
      !newEvent.description.trim()
    ) {
      setAddEventError('Please fill out all fields.');
      return;
    }

    // Image is now optional - no validation needed here

    setLoading(true);
    setAddEventError('');

    try {
      let imageUrl = newEvent.image;

      // If there's an image file, upload it first
      if (imageFile) {
        const formData = new FormData();
        formData.append('imageFile', imageFile);

        const uploadResponse = await fetch(`${API_BASE_URL}/events/upload-image`, {
          method: 'POST',
          body: formData
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          console.error('Upload error:', errorData);
          throw new Error('Failed to upload image');
        }

        const uploadResult = await uploadResponse.json();
        imageUrl = uploadResult.data.url;
      } else {
        // No image provided, set to empty string
        imageUrl = '';
      }

      // Combine date and time for start and end
      const startDateTime = new Date(`${newEvent.startDate}T${newEvent.startTime}`);
      const endDateTime = new Date(`${newEvent.endDate}T${newEvent.endTime}`);

      const payload = {
        Event_Name: newEvent.name,
        start_Datetime: startDateTime.toISOString(),
        end_Datetime: endDateTime.toISOString(),
        Event_Img: imageUrl,
        Description: newEvent.description,
        Submitted_By: `${user?.firstName} ${user?.lastName}`,
        Submitted_Email: user?.email
      };
      const response = await fetch(`${API_BASE_URL}/events/postScheduledEvent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to add event');
      }
      const createdEvent = await response.json();
      onAddEvent(createdEvent);
      setShowAddEvent(false);
      setNewEvent({
        name: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        description: '',
        image: ''
      });
      setImageFile(null);
      setImagePreview(null);
    } catch (err) {
      setAddEventError('Error adding event.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEvent = (event: Event) => {
    setEditEvent(event);
    setEditEventModalOpen(true);
  };

  const handleSaveEvent = async (updatedEvent: Event) => {
    try {
      // Call the API to update the event
      const response = await fetch(`${API_BASE_URL}/events/${updatedEvent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedEvent),
      });

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      // Refresh the parent's data
      if (onEdit) {
        onEdit(0);
      }

      setEditEventModalOpen(false);
      setEditEvent(null);
    } catch (error) {
      console.error('Error updating event:', error);
      // Handle error appropriately
    }
  };

  const handleDeleteEvent = (event: Event) => {
    setEventToDelete(event);
    setDeleteEventModalOpen(true);
  };

  const handleConfirmDelete = async (eventId: number) => {
    try {
      // Call the API to delete the event
      const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete event');
      }

      // Refresh the parent's data
      if (onDelete) {
        onDelete(0);
      }

      setDeleteEventModalOpen(false);
      setEventToDelete(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      // Handle error appropriately
    }
  };

  return (
    <section className="dashboard-section" style={{ marginTop: '20px', maxHeight: 'none' }}>
      <div className="dashboard-section-header">
        <h2 style={{ fontWeight: 700 }}>All Events</h2>
        <button
          className="dashboard-add-btn"
          style={{ fontSize: '0.92rem', fontWeight: 700, padding: '8px 18px' }}
          onClick={() => setShowAddEvent(true)}
        >
          Add Event
        </button>
      </div>
      {/* Filter, Sort, and Search Section */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {/* Status filter on the left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Always show All button */}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: statusFilter === 'All' ? '#e9eaf0' : '#f6f7fb',
              color: statusFilter === 'All' ? '#222' : '#888',
              borderRadius: 20,
              padding: '6px 16px',
              fontSize: '0.95rem',
              fontWeight: statusFilter === 'All' ? 700 : 500,
              border: statusFilter === 'All' ? '2px solid #666' : '1px solid #e0e6f0',
              cursor: statusFilter === 'All' ? 'default' : 'pointer',
              opacity: statusFilter === 'All' ? 1 : 0.85,
              boxShadow: statusFilter === 'All' ? '0 0 0 2px #dbeafe' : undefined,
              marginRight: 8,
              transition: 'box-shadow 0.15s, opacity 0.15s, border-color 0.15s',
              outline: statusFilter === 'All' ? 'none' : undefined
            }}
            onClick={() => statusFilter !== 'All' && setStatusFilter('All')}
          >
            All
            {statusFilter === 'All' && (
              <span style={{ marginLeft: 6, fontWeight: 700, fontSize: '1rem', color: '#43a047', display: 'flex', alignItems: 'center' }}>
                ✓
              </span>
            )}
          </span>

          {/* Show other status buttons */}
          {['Scheduled', 'Completed', 'Pending'].map(status => {
            const isActive = statusFilter === status;
            let background, color, border;
            if (status === 'Pending') {
              background = '#ffe8b3'; color = '#e6a23c'; border = isActive ? '2px solid #d4a017' : '1px solid #ffe0b2';
            } else if (status === 'Scheduled') {
              background = '#d1e7ff'; color = '#3366ff'; border = isActive ? '2px solid #2b5bff' : '1px solid #c3dafe';
            } else if (status === 'Completed') {
              background = '#d4edda'; color = '#43a047'; border = isActive ? '2px solid #2d7d32' : '1px solid #b2f2c3';
            }
            return (
              <span
                key={status}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background,
                  color,
                  borderRadius: 20,
                  padding: '6px 16px',
                  fontSize: '0.95rem',
                  fontWeight: isActive ? 700 : 500,
                  border,
                  cursor: isActive ? 'default' : 'pointer',
                  opacity: isActive ? 1 : 0.85,
                  boxShadow: isActive ? '0 0 0 2px #dbeafe' : undefined,
                  marginRight: 8,
                  transition: 'box-shadow 0.15s, opacity 0.15s, border-color 0.15s',
                  outline: isActive ? 'none' : undefined
                }}
                onClick={() => !isActive && setStatusFilter(status as 'Scheduled' | 'Completed' | 'Pending')}
              >
                {status}
                {isActive && (
                  <span
                    style={{
                      marginLeft: 8,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 2px',
                      lineHeight: 1,
                      width: '16px',
                      height: '16px'
                    }}
                    title="Clear status filter"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusFilter('All');
                    }}
                  >
                    ×
                  </span>
                )}
              </span>
            );
          })}
        </div>
        {/* Sort/search on the right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="sort-order" style={{
              fontWeight: 600,
              fontSize: '0.89rem',
              color: '#555',
              marginRight: 4
            }}>
              Sort by Date:
            </label>
            <select
              id="sort-order"
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value as 'newest' | 'oldest')}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: '0.89rem',
                fontWeight: 500,
                border: '1.5px solid #e0e6f0',
                backgroundColor: '#fff',
                color: '#333',
                cursor: 'pointer',
                outline: 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                minWidth: '140px'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#4f8cff';
                e.target.style.boxShadow = '0 0 0 2px #dbeafe';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e0e6f0';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
          <input
            className="dashboard-search"
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="dashboard-table-wrapper members-table-scroll" style={{ maxHeight: 'none' }}>
        <table className="dashboard-table">
          <thead>
            <tr>
              <th style={{ fontSize: '0.95rem' }}>Name</th>
              <th style={{ fontSize: '0.95rem' }}>Start Date/Time</th>
              <th style={{ fontSize: '0.95rem' }}>End Date/Time</th>
              <th style={{ fontSize: '0.95rem' }}>Description</th>
              <th style={{ fontSize: '0.95rem' }}>Image</th>
              <th style={{ fontSize: '0.95rem' }}>Submitted</th>
              <th style={{ fontSize: '0.95rem' }}>Status</th>
              <th style={{ fontSize: '0.95rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events
              .filter(e => statusFilter === 'All' || (e.status || '') === statusFilter)
              .filter(e =>
                (e.event_Name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (e.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
              )
              .sort((a, b) => {
                const dateA = new Date(a.start_Datetime).getTime();
                const dateB = new Date(b.start_Datetime).getTime();
                return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
              })
              .length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', fontSize: '0.9rem', padding: '40px 20px' }}>
                {statusFilter !== 'All' || searchTerm ?
                  'No events match your current filters. Try adjusting your search or status filter.' :
                  'No events found. Click "Add Event" to create your first event.'
                }
              </td></tr>
            ) : (
              events
                .filter(e => statusFilter === 'All' || (e.status || '') === statusFilter)
                .filter(e =>
                  (e.event_Name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                  (e.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
                )
                .sort((a, b) => {
                  const dateA = a.start_Datetime ? new Date(a.start_Datetime).getTime() : 0;
                  const dateB = b.start_Datetime ? new Date(b.start_Datetime).getTime() : 0;
                  return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
                })
                .map((e, i) => (
                  <tr key={e.id}>
                    <td style={{ fontSize: '0.9rem' }}>{e.event_Name || 'No name'}</td>
                    <td style={{ fontSize: '0.9rem' }}>
                      {e.start_Datetime ? (
                        <div>
                          <div>{new Date(e.start_Datetime).toLocaleDateString()}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            {new Date(e.start_Datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      ) : 'No start time'}
                    </td>
                    <td style={{ fontSize: '0.9rem' }}>
                      {e.end_Datetime ? (
                        <div>
                          <div>{new Date(e.end_Datetime).toLocaleDateString()}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            {new Date(e.end_Datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      ) : 'No end time'}
                    </td>
                    <td style={{ fontSize: '0.9rem' }}>{renderHtmlContent(e.description)}</td>
                    <td style={{ fontSize: '0.9rem' }}>
                      {e.event_Img ? (
                        <img
                          src={e.event_Img}
                          alt={e.event_Name || 'Event'}
                          style={{
                            width: '40px',
                            height: '30px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #e0e6f0',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                          onClick={() => handleEditEvent(e)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          title="Click to edit event"
                        />
                      ) : (
                        <span style={{ color: '#888', fontSize: '0.8rem' }}>No image</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.9rem' }}>
                      {e.submitted_By ? (
                        <div>
                          <div>{e.submitted_By}</div>
                          {e.submitted_Email && (
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>{e.submitted_Email}</div>
                          )}
                          {e.created_At && (
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>{new Date(e.created_At).toLocaleDateString()} {new Date(e.created_At).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#888', fontSize: '0.8rem' }}>Admin</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`dashboard-badge dashboard-badge-${(e.status?.toLowerCase() || 'unknown')}`}
                        style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                        title={`Filter by ${e.status || 'Unknown'}`}
                        onClick={() => setStatusFilter((e.status || 'Unknown') as 'Scheduled' | 'Completed' | 'Pending')}
                      >
                        {e.status || 'Unknown'}
                      </span>
                    </td>
                    <td>
                      <button className="dashboard-icon-btn edit" title="Edit" aria-label="Edit" onClick={() => handleEditEvent(e)}>
                        <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                          <circle cx="10" cy="10" r="10" fill="#eaf3ff" />
                          <path d="M13 7l-6 6v2h2l6-6-2-2z" stroke="#4f8cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                          <path d="M11 5l2 2" stroke="#4f8cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <button className="dashboard-icon-btn delete" title="Delete" aria-label="Delete" onClick={() => handleDeleteEvent(e)}>
                        <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                          <circle cx="10" cy="10" r="10" fill="#fff0f0" />
                          <path d="M6.5 6.5l7 7m0-7l-7 7" stroke="#f36b6b" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>
      {showAddEvent && (
        <div className="dashboard-modal-backdrop">
          <div className="dashboard-modal" style={{ maxWidth: '600px', width: '90vw' }}>
            <h2>Add Event</h2>
            {addEventError && <div className="dashboard-modal-error">{addEventError}</div>}
            <form className="dashboard-modal-form" onSubmit={handleAddEvent}>
              <label>
                Name
                <input type="text" value={newEvent.name} onChange={e => setNewEvent(ev => ({ ...ev, name: e.target.value }))} required />
              </label>
              <label>
                Start Date
                <input type="date" value={newEvent.startDate} onChange={e => setNewEvent(ev => ({ ...ev, startDate: e.target.value }))} required />
              </label>
              <label>
                Start Time
                <input type="time" value={newEvent.startTime} onChange={e => setNewEvent(ev => ({ ...ev, startTime: e.target.value }))} required />
              </label>
              <label>
                End Date
                <input type="date" value={newEvent.endDate} onChange={e => setNewEvent(ev => ({ ...ev, endDate: e.target.value }))} required />
              </label>
              <label>
                End Time
                <input type="time" value={newEvent.endTime} onChange={e => setNewEvent(ev => ({ ...ev, endTime: e.target.value }))} required />
              </label>
              <label>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                  <span>Description</span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: '20px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.querySelector('textarea[name="events-description"]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const selectedText = textarea.value.substring(start, end);

                          if (start === end) {
                            // No text selected, insert placeholder bold text
                            const newValue = newEvent.description.substring(0, start) + '<strong>Bold Text</strong>' + newEvent.description.substring(end);
                            setNewEvent(ev => ({ ...ev, description: newValue }));
                            setTimeout(() => {
                              textarea.focus();
                              // Position cursor at the end of the bold tag (after </strong>)
                              const boldTagLength = '<strong>Bold Text</strong>'.length;
                              textarea.setSelectionRange(start + boldTagLength, start + boldTagLength);
                            }, 100);
                          } else {
                            // Text selected, wrap it with bold tag
                            const newValue = newEvent.description.substring(0, start) + '<strong>' + selectedText + '</strong>' + newEvent.description.substring(end);
                            setNewEvent(ev => ({ ...ev, description: newValue }));
                            setTimeout(() => {
                              textarea.focus();
                              // Position cursor at the end of the bold tag (after </strong>)
                              const boldTagLength = '<strong>'.length + selectedText.length + '</strong>'.length;
                              textarea.setSelectionRange(start + boldTagLength, start + boldTagLength);
                            }, 100);
                          }
                        }
                      }}
                      style={{
                        background: '#ffe6e6',
                        color: '#d32f2f',
                        border: '1px solid #d32f2f',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontWeight: 'bold',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ffcccc';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#ffe6e6';
                      }}
                      title="Make selected text bold"
                    >
                      <strong>B</strong> Bold Text
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.querySelector('textarea[name="events-description"]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const currentValue = newEvent.description;
                          const newValue = currentValue.substring(0, start) + '</br>' + currentValue.substring(end);
                          setNewEvent(ev => ({ ...ev, description: newValue }));

                          // Set cursor position after the inserted tag
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(start + 5, start + 5);
                          }, 100);
                        }
                      }}
                      style={{
                        background: '#f0f4ff',
                        color: '#3366ff',
                        border: '1px solid #3366ff',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e6f0ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      title="+ Add Break to description"
                    >
                  + Line Break
                  </button>
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.querySelector('textarea[name="events-description"]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const selectedText = textarea.value.substring(start, end);

                          if (start === end) {
                            // No text selected, insert placeholder link
                            const newValue = newEvent.description.substring(0, start) + '<a href="https://example.com">Link Text</a>' + newEvent.description.substring(end);
                            setNewEvent(ev => ({ ...ev, description: newValue }));
                            setTimeout(() => {
                              textarea.focus();
                              // Position cursor at the end of the link tag (after </a>)
                              const linkTagLength = '<a href="https://example.com">Link Text</a>'.length;
                              textarea.setSelectionRange(start + linkTagLength, start + linkTagLength);
                            }, 100);
                          } else {
                            // Text selected, wrap it with link tag
                            const newValue = newEvent.description.substring(0, start) + '<a href="https://example.com">' + selectedText + '</a>' + newEvent.description.substring(end);
                            setNewEvent(ev => ({ ...ev, description: newValue }));
                            setTimeout(() => {
                              textarea.focus();
                              // Position cursor at the end of the link tag (after </a>)
                              const linkTagLength = '<a href="https://example.com">'.length + selectedText.length + '</a>'.length;
                              textarea.setSelectionRange(start + linkTagLength, start + linkTagLength);
                            }, 100);
                          }
                        }
                      }}
                      style={{
                        background: '#fffbf0',
                        color: '#b8860b',
                        border: '1px solid #b8860b',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fff8e6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fffbf0';
                      }}
                      title="Make selected text a hyperlink"
                    >
                      🔗 Make Link
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <textarea
                    name="events-description"
                    value={newEvent.description}
                    onChange={e => setNewEvent(ev => ({ ...ev, description: e.target.value }))}
                    required
                    placeholder="Enter event description..."
                    style={{ minHeight: '80px', width: '90%' }}
                  />
                </div>
              </label>
              <label>
                Event Image (Optional)
                <div
                  style={{
                    border: '2px dashed #e0e6f0',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    margin: '0 8px'
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={imagePreview}
                        alt="Event preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          borderRadius: '4px',
                          border: '1px solid #e0e6f0'
                        }}
                      />
                      {imageFile && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage();
                          }}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: '#f36b6b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Remove image"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '24px', marginBottom: '8px', color: '#666' }}>📷</div>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                        Click to upload or drag and drop
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        PNG, JPG, GIF up to 5MB
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </label>
              <div className="dashboard-modal-actions">
                <button type="button" className="dashboard-modal-cancel" onClick={() => {
                  setShowAddEvent(false);
                  setAddEventError('');
                  setNewEvent({
                    name: '',
                    startDate: '',
                    startTime: '',
                    endDate: '',
                    endTime: '',
                    description: '',
                    image: ''
                  });
                  setImageFile(null);
                  setImagePreview(null);
                }}>Cancel</button>
                <button type="submit" className="dashboard-modal-save" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {editEventModalOpen && editEvent && (
        <EditEventModal
          event={editEvent}
          isOpen={editEventModalOpen}
          onClose={() => setEditEventModalOpen(false)}
          onSave={handleSaveEvent}
        />
      )}
      {deleteEventModalOpen && eventToDelete && (
        <DeleteEventModal
          event={eventToDelete}
          isOpen={deleteEventModalOpen}
          onClose={() => setDeleteEventModalOpen(false)}
          onConfirm={() => handleConfirmDelete(eventToDelete.id)}
        />
      )}
    </section>
  );
};

export default EventsPage;
